import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { kitPictureSrc } from '../kitPictureSrc';
import { postFileLabel, postFileThumbPicture, PostsBoardFile, PostsBoardSong } from './postsModel';
import { postsMessages } from './postsMessages';
import { PostsFailedAction } from './PostsFailedAction';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Posts.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PostsMakingStripProps {
  accountId: number;
  files: PostsBoardFile[];
  songs: Record<string, PostsBoardSong>;
  catalogByUuid: ReadonlyMap<string, CatalogItem>;
  readOnly: boolean;
  /** "See" of the phone line (design c3): the release page of the first file. */
  onSee: (file: PostsBoardFile) => void;
}

/**
 * The Making strip on top of the board (design assets-rethink c1 `.mkstrip`): the post files that
 * are queued or made now, with the percent, and the open failures with "credits back" and Try
 * again. It hides when empty. Under 576 px it is one line "Making 3 · 1 failed · See" (c3).
 */
const PostsMakingStripComponent: React.FC<PostsMakingStripProps> = (props) => {
  const { accountId, files, songs, catalogByUuid, readOnly, onSee } = props;
  const intl = useIntl();
  const failedCount = useMemo(() => files.filter((file) => file.state === 'failed').length, [files]);
  const first = files[0];
  if (first === undefined) {
    return null;
  }

  const detail = (file: PostsBoardFile): string => {
    const song =
      file.catalogItemUuid === null
        ? intl.formatMessage(postsMessages.noSong)
        : (songs[file.catalogItemUuid]?.title ?? intl.formatMessage(postsMessages.noSong));
    if (file.state === 'failed') {
      return intl.formatMessage(postsMessages.makingFailed, { song });
    }
    if (file.state === 'queued') {
      return intl.formatMessage(postsMessages.makingQueued, { song });
    }
    return file.progress > 0
      ? intl.formatMessage(postsMessages.makingPercent, { song, percent: file.progress })
      : intl.formatMessage(postsMessages.makingWorking, { song });
  };

  return (
    <div className={styles.making} data-testid="posts-making">
      <span className={styles.eyebrow}>{intl.formatMessage(postsMessages.making, { count: files.length })}</span>
      <span className={styles.makingPhone}>
        {intl.formatMessage(postsMessages.makingPhone, { count: files.length, failed: failedCount })}
        <button type="button" className={styles.link} onClick={() => onSee(first)}>
          {intl.formatMessage(postsMessages.makingSee)}
        </button>
      </span>
      {files.map((file) => {
        const thumb = postFileThumbPicture(file);
        const catalogItem = file.catalogItemUuid === null ? undefined : catalogByUuid.get(file.catalogItemUuid);
        return (
          <div key={file.key} className={clsx(styles.makingCard, file.state === 'failed' && styles.makingBad)}>
            {thumb === null ? (
              <span className={clsx(styles.thumb, styles.thumbSmall)} />
            ) : (
              <img className={clsx(styles.thumb, styles.thumbSmall)} src={kitPictureSrc(thumb, 32)} alt="" />
            )}
            <div>
              <b>{postFileLabel(intl, file)}</b>
              <small>{detail(file)}</small>
            </div>
            {file.state === 'failed' && !readOnly && catalogItem !== undefined && (
              <PostsFailedAction accountId={accountId} file={file} catalogItem={catalogItem} />
            )}
          </div>
        );
      })}
    </div>
  );
};

export const PostsMakingStrip = TypedMemo(PostsMakingStripComponent);
